import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GardenObject } from "../types";
import ImageGenerator from "./ImageGenerator";
import "./ImageGenerator.css";

interface ObjectEditFormProps {
  object: GardenObject | null;
  _onSave: (object: GardenObject) => void;
  objectTypes: string[];
  categories: string[];
  setObjectTypes: (types: string[]) => void;
  setCategories: (categories: string[]) => void;
}

const ObjectEditForm: React.FC<ObjectEditFormProps> = ({
  object,
  _onSave,
  objectTypes: parentObjectTypes,
  categories: parentCategories,
  setObjectTypes,
  setCategories,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<GardenObject>({
    object_id: "",
    name: "",
    type: "",
    category: "",
    image_url: "",
    tags: [],
  });

  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");

  // Track original values to preserve unique types/categories
  const [originalType, setOriginalType] = useState<string>("");
  const [originalCategory, setOriginalCategory] = useState<string>("");

  // Catalog settings state
  const [loadingSettings, setLoadingSettings] = useState<boolean>(
    parentObjectTypes.length === 0
  );
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Fetch catalog settings from API
  const fetchCatalogSettings = async () => {
    setLoadingSettings(true);
    setSettingsError(null);

    // Fallback to default values
    const defaultTypes = [
      "furniture",
      "structure",
      "tool",
      "decoration",
      "material",
    ];
    const defaultCategories = [
      "outdoor",
      "indoor",
      "tools",
      "materials",
      "decorations",
    ];
    setObjectTypes(defaultTypes);
    setCategories(defaultCategories);
    setLoadingSettings(false);
  };

  useEffect(() => {
    // Store original values to preserve unique types/categories
    setOriginalType(object?.type || "");
    setOriginalCategory(object?.category || "");
  }, []);

  useEffect(() => {
    // Only fetch if we don't already have the data
    if (parentObjectTypes.length === 0 || parentCategories.length === 0) {
      fetchCatalogSettings();
    }
  }, [parentObjectTypes.length, parentCategories.length]);

  useEffect(() => {
    if (object) {
      setFormData({
        ...object,
        image_url: object.image_url || formData.image_url || "",
        tags: object.tags || tags || [],
      });
      // Initialize tags from object data or use default empty array
      setTags(object.tags || []);
    }
  }, [object]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedFormData);
    _onSave({ ...updatedFormData });
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");

      const updatedFormData = {
        ...formData,
        tags: [...tags, newTag.trim()],
      };
      setFormData(updatedFormData);
      _onSave({ ...updatedFormData });
    }
  };

  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);

    const updatedFormData = {
      ...formData,
      tags: updatedTags,
    };
    setFormData(updatedFormData);
    _onSave({ ...updatedFormData });
  };

  const handleImageGenerated = (imageUrl: string) => {
    setFormData((prevState) => ({
      ...prevState,
      image_url: imageUrl,
    }));
  };

  return (
    <div className="object-edit-form">
      {settingsError && (
        <div className="alert alert-warning" role="alert">
          {settingsError} - {t("admin.errorMessages.usingDefaultValues")}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">{t("forms.labels.name")}</label>
        <input
          type="text"
          className="form-control"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="object_type">{t("forms.labels.type")}</label>
        <select
          className="form-select"
          id="object_type"
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          required
          disabled={loadingSettings}
        >
          <option value="">
            {loadingSettings ? t("admin.formLabels.loadingTypes") : t("admin.formLabels.selectType")}
          </option>
          {/* Show original type if it exists and isn't in the loaded types yet */}
          {originalType && !parentObjectTypes.includes(originalType) && (
            <option value={originalType}>{originalType} ({t("admin.formLabels.original")})</option>
          )}
          {parentObjectTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="category">{t("forms.labels.category")}</label>
        <select
          className="form-select"
          id="category"
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          required
          disabled={loadingSettings}
        >
          <option value="">
            {loadingSettings ? t("admin.formLabels.loadingCategories") : t("admin.formLabels.selectCategory")}
          </option>
          {/* Show original category if it exists and isn't in the loaded categories yet */}
          {originalCategory && !parentCategories.includes(originalCategory) && (
            <option value={originalCategory}>
              {originalCategory} ({t("admin.formLabels.original")})
            </option>
          )}
          {parentCategories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>{t("admin.formLabels.tags")}</label>
        <div className="tags-container">
          {tags.map((tag) => (
            <div key={tag} className="tag">
              {tag}
              <span className="remove-tag" onClick={() => removeTag(tag)}>
                ×
              </span>
            </div>
          ))}
        </div>
        <div className="add-tag-input">
          <input
            type="text"
            className="form-control"
            placeholder={t("admin.formLabels.addTagPlaceholder")}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addTag()}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={addTag}
          >
            {t("admin.formLabels.addTag")}
          </button>
        </div>
      </div>

      {/* Image Generator */}
      <ImageGenerator
        initialImageUrl={formData.image_url}
        itemName={formData.name || "garden object"}
        itemType="object"
        itemId={formData.object_id}
        onImageGenerated={handleImageGenerated}
      />
    </div>
  );
};

export default ObjectEditForm;